import React, { useMemo, useState } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import {
  DashboardData,
  DashboardPeriod,
  buildRevenueYTicks,
  computeChartMax,
  formatCurrency,
} from '../../../services/dashboard';
import {
  bottomCardSx,
  cardBodySx,
  cardHeaderSx,
  cardSubtitleSx,
  cardTitleSx,
  chartTooltipProps,
  dashboardTokens,
  dropdownSx,
  filledBarSx,
  ghostBarSx,
} from './dashboardCardStyles';

type RevenueChartProps = {
  revenue: DashboardData['revenue'];
  loading?: boolean;
  onPeriodChange: (period: DashboardPeriod) => void;
};

const CHART_HEIGHT = 155;
const SLOT_HEIGHT = 120;
const Y_AXIS_WIDTH = 44;
const BAR_WIDTH = 14;
const BAR_GAP = 6;

const formatAxisLabel = (value: number): string => {
  if (value >= 1000) {
    const k = value / 1000;
    return Number.isInteger(k) ? `${k}k` : `${k.toFixed(1)}k`;
  }
  return String(value);
};

export function RevenueChart({
  revenue,
  loading = false,
  onPeriodChange,
}: RevenueChartProps) {
  const { weekly, total, allTimeTotal, period = 'weekly' } = revenue;
  const [showIncome, setShowIncome] = useState(true);
  const [showExpenses, setShowExpenses] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const peak = useMemo(() => {
    let max = 0;
    weekly.forEach((p) => {
      if (showIncome) max = Math.max(max, p.income);
      if (showExpenses) max = Math.max(max, p.expenses);
    });
    return max;
  }, [weekly, showIncome, showExpenses]);

  const chartMax = useMemo(() => computeChartMax(peak), [peak]);
  const yTicks = useMemo(() => buildRevenueYTicks(chartMax), [chartMax]);
  const scale = SLOT_HEIGHT / chartMax;
  const displayTotal = total > 0 ? total : allTimeTotal;
  const periodLabel = period === 'monthly' ? 'Monthly' : 'Weekly';
  const periodIncome = weekly.reduce((s, p) => s + p.income, 0);
  const periodPending = weekly.reduce((s, p) => s + p.expenses, 0);

  const barGroupWidth = showIncome && showExpenses ? BAR_WIDTH * 2 + BAR_GAP : BAR_WIDTH;

  return (
    <Box sx={{ ...bottomCardSx, opacity: loading ? 0.65 : 1, transition: 'opacity 0.2s' }}>
      <Box sx={cardHeaderSx}>
        <Box>
          <Typography sx={cardTitleSx}>Revenue</Typography>
          <Typography sx={cardSubtitleSx}>
            {periodLabel} paid income · {formatCurrency(periodPending)} pending
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showIncome}
              onChange={(e) => setShowIncome(e.target.checked)}
              style={{ accentColor: '#fff', width: 14, height: 14 }}
            />
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: '#fff', lineHeight: 1 }}>
              Income
            </Typography>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showExpenses}
              onChange={(e) => setShowExpenses(e.target.checked)}
              style={{ accentColor: '#888', width: 14, height: 14 }}
            />
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: '#888', lineHeight: 1 }}>
              Pending
            </Typography>
          </label>
          <Box onClick={(e) => setAnchorEl(e.currentTarget)} sx={dropdownSx}>
            {periodLabel}
            <KeyboardArrowDown sx={{ fontSize: 16, color: '#bbb' }} />
          </Box>
        </Box>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem selected={period === 'weekly'} onClick={() => { onPeriodChange('weekly'); setAnchorEl(null); }}>
          Weekly
        </MenuItem>
        <MenuItem selected={period === 'monthly'} onClick={() => { onPeriodChange('monthly'); setAnchorEl(null); }}>
          Monthly
        </MenuItem>
      </Menu>

      <Typography sx={{ fontWeight: 700, fontSize: 26, color: '#fff', mt: 1.25, mb: 0.5, lineHeight: 1.1 }}>
        {formatCurrency(displayTotal)}
      </Typography>
      <Typography sx={{ fontSize: 11, color: dashboardTokens.textMuted, mb: 1.25 }}>
        Period income {formatCurrency(periodIncome)}
      </Typography>

      <Box sx={{ ...cardBodySx, position: 'relative', pl: `${Y_AXIS_WIDTH}px` }}>
        <Box sx={{ position: 'absolute', left: 0, top: 0, width: Y_AXIS_WIDTH, height: CHART_HEIGHT - 22 }}>
          {yTicks.map((val) => (
            <Typography
              key={val}
              sx={{
                position: 'absolute',
                top: `${SLOT_HEIGHT - val * scale - 8}px`,
                left: 0,
                width: Y_AXIS_WIDTH - 6,
                textAlign: 'right',
                color: '#888',
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              {formatAxisLabel(val)}
            </Typography>
          ))}
        </Box>

        <Box sx={{ position: 'absolute', left: Y_AXIS_WIDTH, right: 0, top: 0, height: SLOT_HEIGHT }}>
          {yTicks.map((val) => (
            <Box
              key={val}
              sx={{
                position: 'absolute',
                top: `${SLOT_HEIGHT - val * scale}px`,
                left: 0,
                right: 0,
                borderTop: '1px solid #2c2c2c',
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.max(weekly.length, 1)}, 1fr)`,
            alignItems: 'end',
            height: CHART_HEIGHT,
            gap: 0.25,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {weekly.map((point, index) => {
            const label = point.label || point.day;
            const incomeFill = point.income > 0 ? Math.max(point.income * scale, 3) : 0;
            const expenseFill = point.expenses > 0 ? Math.max(point.expenses * scale, 3) : 0;
            const net = point.income - point.expenses;

            const tooltipTitle = (
              <Box sx={{ lineHeight: 1.55 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>{label}</Typography>
                <Typography sx={{ fontSize: 12, color: '#ccc' }}>
                  Income: <strong>{formatCurrency(point.income)}</strong>
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#aaa' }}>
                  Pending: <strong>{formatCurrency(point.expenses)}</strong>
                </Typography>
                <Typography sx={{ fontSize: 11, color: net >= 0 ? '#7dcea0' : '#e57373', mt: 0.25 }}>
                  Net: {formatCurrency(net)}
                </Typography>
              </Box>
            );

            return (
              <Tooltip key={point.dateKey || `${label}-${index}`} title={tooltipTitle} {...chartTooltipProps}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: 0,
                    cursor: 'default',
                    '&:hover .rev-ghost': { bgcolor: dashboardTokens.ghostBarHover },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      gap: `${BAR_GAP}px`,
                      height: SLOT_HEIGHT,
                      mb: 0.75,
                      width: barGroupWidth,
                    }}
                  >
                    {showIncome && (
                      <Box sx={{ ...ghostBarSx(SLOT_HEIGHT, BAR_WIDTH), className: 'rev-ghost' }}>
                        {incomeFill > 0 && (
                          <Box sx={filledBarSx(incomeFill, dashboardTokens.income)} />
                        )}
                      </Box>
                    )}
                    {showExpenses && (
                      <Box sx={{ ...ghostBarSx(SLOT_HEIGHT, BAR_WIDTH), className: 'rev-ghost' }}>
                        {expenseFill > 0 && (
                          <Box sx={filledBarSx(expenseFill, dashboardTokens.expense)} />
                        )}
                      </Box>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: point.income + point.expenses > 0 ? '#bbb' : '#555',
                      fontWeight: 600,
                      textAlign: 'center',
                      lineHeight: 1.2,
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2.5, flexShrink: 0, pt: 0.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '4px', bgcolor: '#fff' }} />
          <Typography sx={{ fontSize: 12, color: '#888' }}>Income</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '4px', bgcolor: '#6b6b6b' }} />
          <Typography sx={{ fontSize: 12, color: '#888' }}>Pending</Typography>
        </Box>
      </Box>
    </Box>
  );
}
